<template>
  <div class="w-full h-full lens-spend-back-container">
    <BaseLoadingLogo v-if="loading.status" />

    <LensSpendBackDashboard
      v-else-if="isOnboardComplete && !showCompleteScreen"
      :lens-id="lensId"
      :lens="lens"
      :accounts="metaAccounts"
    />

    <LensSpendBackComplete
      v-else-if="showCompleteScreen"
      @open-dashboard="showCompleteScreen = false"
    />

    <LensSpendBackOnboarding
      v-else-if="showOnboarding"
      :lens-id="lensId"
      :lens="lens"
      :accounts="metaAccounts"
      :initial-onboard-status="onboardStatus"
      :property-id="propertyId"
      :loading="accountsLoading"
      @step-complete="handleStepComplete"
      @complete="handleComplete"
    />

    <LensSpendBackEmptyState
      v-else
      :lens="lens"
      :accounts="metaAccounts"
      :total-monthly-spend="metaMonthlySpend"
      :loading="accountsLoading"
      @get-started="showOnboarding = true"
    />
  </div>
</template>

<script>
import LensAPI from '@/api/lens'
import LensSpendBackComplete from '../../components/lens/spendBack/LensSpendBackComplete.vue'
import LensSpendBackDashboard from '../../components/lens/spendBack/LensSpendBackDashboard.vue'
import LensSpendBackEmptyState from '../../components/lens/spendBack/LensSpendBackEmptyState.vue'
import LensSpendBackOnboarding from '../../components/lens/spendBack/LensSpendBackOnboarding.vue'

export default {
  name: 'LensSpendBackView',
  components: {
    LensSpendBackComplete,
    LensSpendBackDashboard,
    LensSpendBackEmptyState,
    LensSpendBackOnboarding
  },
  props: {
    lensId: {
      type: String,
      required: true
    },
    lens: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: {
        status: true
      },
      accountsLoading: true,
      accounts: [],
      totalMonthlySpend: 0,
      showOnboarding: false,
      // Shown once, right after the last onboarding step
      showCompleteScreen: false,
      propertyId: null,
      onboardStatus: null
    }
  },
  computed: {
    isOnboardComplete () {
      return this.onboardStatus === 'completed'
    },
    // Spendback only supports Meta for now
    metaAccounts () {
      return this.accounts.filter(account => account.provider === 'facebook')
    },
    metaMonthlySpend () {
      return this.metaAccounts.reduce((sum, account) => sum + (Number(account.spend) || 0), 0)
    }
  },
  watch: {
    lensId: {
      immediate: true,
      handler (newVal) {
        if (newVal) {
          this.fetchLensSpend()
          this.fetchLensSbStatus()
        }
      }
    }
  },
  methods: {
    async fetchLensSbStatus () {
      this.loading.status = true
      try {
        const { data } = await LensAPI.Lens.SpendBack.getStatus(this.lensId)
        this.propertyId = data?.property_id || null
        this.onboardStatus = data?.onboard_step || null
        if (this.onboardStatus) {
          this.showOnboarding = true
        }
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error?.response?.data?.message || 'Could not load your Spendback setup. Please refresh and try again.'
        })
        this.propertyId = null
        this.onboardStatus = null
      } finally {
        this.loading.status = false
      }
    },
    async fetchLensSpend () {
      this.accountsLoading = true
      try {
        const { data } = await LensAPI.Lens.getMonthlySpend(this.lensId)
        this.accounts = data.ad_accounts || []
        this.totalMonthlySpend = Number(data.total_spend) || 0
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: error?.response?.data?.message || 'Could not load your ad accounts. Please refresh and try again.'
        })
        this.accounts = []
        this.totalMonthlySpend = 0
      } finally {
        this.accountsLoading = false
      }
    },
    handleStepComplete ({ stepKey, stepIndex, data }) {
      // TODO: write step completion to Firestore for this.lensId
    },
    handleComplete () {
      this.onboardStatus = 'completed'
      this.showOnboarding = false
      this.showCompleteScreen = true
    }
  }
}
</script>

<style scoped>
.lens-spend-back-container {
  position: relative;
  isolation: isolate;
}
</style>
